import React from "react";
import { RouteComponentProps } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import "./Feed.css";
import "../../App.css";
import { fetchRetry } from "../../functions/request";
import { apiRequest } from "../../functions/requestMulti";
import Title from "../../components/Title";
import {
  addIdToObject,
  dateToStr,
  dateTosimpleDateStr,
  firebaseDateToDateObj,
  objectToArray,
} from "../../functions/utils";
import qs from "qs";
import SingleSelector from "../../components/SingleSelector";
import { MainContext } from "../../contexts/MainContext";
import {
  ListCacheEntry,
  restoreListState,
  restoreScrollPosition,
  saveListState,
} from "../../functions/listStateCache";
import FeedEntry from "../../components/FeedEntry";

// InfiniteScroll gets a `height` here, so the feed scrolls inside its own
// element rather than the shell's `.br-inner-container`.
const FEED_SCROLLER = ".infinite-scroll-component";

// The v2 elements API returns at most 10 courses and 10 subscriptions per page
// (per_page in elementRepository); its hitsPerPage is only validated.
const ELEMENT_PAGE_SIZE = 10;

type ComposedProps = RouteComponentProps<{
  clubId: string;
  branchId: string;
}>;

type FeedTypes =
  | "course-booked"
  | "course-canceled"
  | "course-refunded"
  | "membership-booked"
  | "membership-canceled"
  | "membership-refunded";

export type FeedEntrys = {
  id: string;
  createdAt: Date;
  clubId: string;
  branchId: string;
  courseBooking: string;
  userId: string | null;
  message: string;
  courseId: string | null;
  subscriptionId: string | null;
  tagArr: Array<string>;
  type: FeedTypes;
};

export default class Feed extends React.Component<
  ComposedProps,
  {
    clubId: string;
    branchId: string;
    feedEntrys: Array<FeedEntrys>;
    feedLoaded: boolean;
    batchSize: number;
    hasMore: boolean;
    selectedUserId: string | null | any;
    selectedElementId: string | null | any;
    selectedType: string | null | any;
    selectedEventTypeElem: any;
    usersLoaded: boolean;
    userOptions: Array<any>;
    selectedUserElem: any;
    selectedElement: any;
    elementSearch: string;
    elementPage: number;
    elementHasMore: boolean;
    elementsLoading: boolean;
    subscriptions: Array<any>;
    futureCourses: Array<any>;
    pastCourses: Array<any>;
  }
> {
  static contextType = MainContext;

  private cacheKey: string;
  private restored: ListCacheEntry | null;
  private leavingToDetail = false;
  private cancelScroll: (() => void) | null = null;
  private mounted = false;
  private rootRef = React.createRef<HTMLDivElement>();
  private elementSearchTimeout: ReturnType<typeof setTimeout> | null = null;
  private elementRequestId = 0;

  constructor(props: ComposedProps) {
    super(props);
    this.cacheKey = props.match.url;
    this.restored = restoreListState(this.cacheKey);
    const query: any = qs.parse(props.location.search, {
      ignoreQueryPrefix: true,
    });
    // Filters are URL-owned (requestNewQuery pushes them), so the URL wins -
    // but only where it actually carries a value. A FeedEntry's back link is a
    // bare .../feed with no query, so overwriting unconditionally would wipe
    // the restored filters.
    const urlFilters: any = {};
    if (query.suserId !== undefined)
      urlFilters.selectedUserId = query.suserId || null;
    if (query.selemId !== undefined)
      urlFilters.selectedElementId = query.selemId || null;
    if (query.stype !== undefined)
      urlFilters.selectedType = query.stype || null;
    this.state = {
      clubId: this.props.match.params.clubId,
      branchId: this.props.match.params.branchId,
      feedEntrys: [],
      feedLoaded: false,
      batchSize: 15,
      hasMore: true,
      selectedUserId: null,
      selectedElementId: null,
      selectedType: null,
      selectedEventTypeElem: { name: "alle", value: null },
      usersLoaded: false,
      userOptions: [],
      selectedUserElem: { name: "alle", value: null },
      selectedElement: { name: "alle", value: null },
      elementSearch: "",
      elementPage: 0,
      elementHasMore: false,
      elementsLoading: false,
      subscriptions: [],
      futureCourses: [],
      pastCourses: [],
      // Returning from a feed entry: render the entries already loaded plus
      // the active filters instead of starting over at the newest 15.
      ...(this.restored ? this.restored.state : {}),
      ...urlFilters,
    };
  }

  componentDidMount = () => {
    this.mounted = true;
    if (this.restored) {
      this.cancelScroll = restoreScrollPosition(
        this.restored,
        this.rootRef.current,
        5,
        undefined,
        FEED_SCROLLER
      );
    } else {
      // Skipped when restoring: requestFeed lowers feedLoaded (skeletons) and
      // appends a second copy of the newest batch. Feed entries are an
      // append-only log, so the restored rows cannot go stale - only new
      // entries at the top are missed, and prepending those would push the
      // list down and cost the user their scroll position.
      this.requestFeed();
    }
    // Reference data for the filter dropdowns. These never lower the feed's
    // loading flag, so they are silent, and they re-derive the selected labels
    // from the restored filter ids.
    this.requestUsers();
    this.requestElements(0);
    this.requestSelectedElement();
  };

  componentWillUnmount = () => {
    this.mounted = false;
    if (this.cancelScroll) this.cancelScroll();
    if (this.elementSearchTimeout) clearTimeout(this.elementSearchTimeout);
    if (this.leavingToDetail) {
      saveListState(
        this.cacheKey,
        {
          feedEntrys: this.state.feedEntrys,
          hasMore: this.state.hasMore,
          selectedUserId: this.state.selectedUserId,
          selectedElementId: this.state.selectedElementId,
          selectedType: this.state.selectedType,
          selectedEventTypeElem: this.state.selectedEventTypeElem,
          selectedUserElem: this.state.selectedUserElem,
          selectedElement: this.state.selectedElement,
          userOptions: this.state.userOptions,
          feedLoaded: true,
          usersLoaded: true,
        },
        this.rootRef.current,
        FEED_SCROLLER
      );
    }
  };

  /**
   * Feed entries navigate through <Link>s inside FeedEntry, so there is no
   * single redirect handler to hook. A capture-phase listener on the screen
   * catches all of them - and ignores sidebar navigation, which happens
   * outside this subtree.
   */
  handleListClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target && target.closest && target.closest("a")) {
      this.leavingToDetail = true;
    }
  };

  requestFeed = () => {
    this.setState({ feedLoaded: false });
    fetchRetry(
      "getBranchFeed",
      {
        clubId: this.state.clubId,
        branchId: this.state.branchId,
        startAfterDocId:
          this.state.feedEntrys.length > 0
            ? this.state.feedEntrys[this.state.feedEntrys.length - 1].id
            : null,
        userId: this.state.selectedUserId,
        elemId: this.state.selectedElementId,
        type: this.state.selectedType,
        limit: this.state.batchSize,
      },
      1,
      5
    )
      .then(this.handleBranchFeed)
      .catch(this.handleError);
  };

  requestUsers = () => {
    fetchRetry(
      "getUsersFromBranch",
      {
        clubId: this.state.clubId,
        branchId: this.state.branchId,
      },
      1,
      5
    )
      .then(this.handleUsers)
      .catch(this.handleError);
  };

  /**
   * The Kurs/Mitgliedschaft filter searches through the v2 API (Typesense)
   * instead of loading every element of the branch up front: weekly offerings
   * pile up thousands of past single courses, more than any fixed limit on
   * getCoursesOfBranch covers. The API pages courses either upcoming or past,
   * so each page asks for both.
   */
  requestElements = async (page: number) => {
    const requestId = ++this.elementRequestId;
    this.setState({ elementsLoading: true });
    try {
      const [upcoming, past] = await Promise.all([
        this.fetchElementPage(page, false),
        this.fetchElementPage(page, true),
      ]);
      // A newer search started while this one was in flight.
      if (!this.mounted || requestId !== this.elementRequestId) return;

      const isSubscription = (element: any) =>
        element.courseType === "subscription";
      // Both requests return the same subscription page.
      const subscriptions = upcoming.filter(isSubscription);
      const futureCourses = upcoming.filter((e: any) => !isSubscription(e));
      const pastCourses = past.filter((e: any) => !isSubscription(e));
      this.setState({
        elementPage: page,
        elementHasMore: [subscriptions, futureCourses, pastCourses].some(
          (arr) => arr.length >= ELEMENT_PAGE_SIZE
        ),
        elementsLoading: false,
        subscriptions:
          page === 0
            ? subscriptions
            : this.state.subscriptions.concat(subscriptions),
        futureCourses:
          page === 0
            ? futureCourses
            : this.state.futureCourses.concat(futureCourses),
        pastCourses:
          page === 0 ? pastCourses : this.state.pastCourses.concat(pastCourses),
      });
    } catch (err) {
      if (this.mounted && requestId === this.elementRequestId) {
        this.setState({ elementsLoading: false });
      }
      this.handleError(err);
    }
  };

  fetchElementPage = async (page: number, showPast: boolean) => {
    const { data } = await apiRequest("api/elements/branch", "GET", {
      clubId: this.state.clubId,
      branchId: this.state.branchId,
      query: this.state.elementSearch,
      page,
      hitsPerPage: ELEMENT_PAGE_SIZE,
      showDeleted: false,
      showPast,
      tag: "",
      onlySubscriptions: false,
    });
    return data.success ? data.data : [];
  };

  /**
   * A filter from the URL (reload, shared link) still needs its label, and
   * the element is rarely among the first search results. The id alone does
   * not say whether it is a course or a subscription, so ask for both.
   */
  requestSelectedElement = async () => {
    const elemId = this.state.selectedElementId;
    if (!elemId || this.state.selectedElement?.value === elemId) return;
    const params = {
      clubId: this.state.clubId,
      branchId: this.state.branchId,
      targetUserId: this.context.selectedUserId,
    };
    const results = await Promise.all([
      fetchRetry("getCourseFromIdFNew", { ...params, courseId: elemId }, 1, 5),
      fetchRetry(
        "getSubscriptionFromIdF",
        { ...params, subscriptionId: elemId },
        1,
        5
      ),
    ]).catch(this.handleError);
    if (!results || !this.mounted) return;
    if (this.state.selectedElementId !== elemId) return;

    const [courseRes, subscriptionRes]: any = results;
    if (courseRes.data.success) {
      this.setState({
        selectedElement: this.courseOption({
          ...courseRes.data.data[0],
          id: elemId,
        }),
      });
    } else if (subscriptionRes.data.success) {
      this.setState({
        selectedElement: this.subscriptionOption({
          ...subscriptionRes.data.data[0],
          id: elemId,
        }),
      });
    }
  };

  handleElementSearch = (elementSearch: string) => {
    if (this.elementSearchTimeout) clearTimeout(this.elementSearchTimeout);
    this.setState({ elementSearch });
    this.elementSearchTimeout = setTimeout(() => this.requestElements(0), 300);
  };

  handleLoadMoreElements = () => {
    this.requestElements(this.state.elementPage + 1);
  };

  subscriptionOption = (subscription: any) => ({
    name: subscription.title,
    value: subscription.id,
    color: "#5c67ee",
  });

  // Weekly offerings are separate single courses sharing one title, so the
  // date is what tells them apart.
  courseOption = (course: any) => {
    const start = firebaseDateToDateObj(course.startTime);
    return {
      name:
        course.courseType !== "infinity-course" && start
          ? `${course.title} (${dateToStr(start)})`
          : course.title,
      value: course.id,
      color: "#ee5c5c",
    };
  };

  getElementOptions = () => {
    const elementOptions: Array<any> = [
      { name: "alle", value: null, color: "#ffd025" },
    ];
    // Offset paging can return an element twice when courses change between
    // pages, and a course ending right now matches both time ranges.
    const seen: { [id: string]: boolean } = {};
    const add = (option: any) => {
      if (seen[option.value]) return;
      seen[option.value] = true;
      elementOptions.push(option);
    };
    this.state.subscriptions.forEach((subscription: any) =>
      add(this.subscriptionOption(subscription))
    );
    this.state.futureCourses
      .concat(this.state.pastCourses)
      .forEach((course: any) => add(this.courseOption(course)));
    return elementOptions;
  };

  handleUsers = ({ data }: any) => {
    if (!this.mounted) return;
    this.setState({ usersLoaded: true });
    if (data.success) {
      const users = addIdToObject(data.data);
      const userArr = objectToArray(users);
      const userOptions = userArr.map((user: any) => {
        return {
          name: `${user.fName} ${user.lName}`,
          value: user.id,
          color: "#ee5c5c",
        };
      });
      userOptions.unshift({ name: "alle", value: null, color: "#ffd025" });
      userOptions.forEach((option: any) => {
        if (option.value === this.state.selectedUserId) {
          this.setState({ selectedUserElem: option });
        }
      });
      this.setState({ userOptions });
    }
  };

  handleBranchFeed = ({ data }: any) => {
    if (!this.mounted) return;
    if (data.success) {
      const feedEntrys = this.state.feedEntrys;
      const feedIds = feedEntrys.map((entry: any) => entry.id);
      const feed = feedEntrys.concat(
        data.data.filter((entry: any) => !feedIds.includes(entry.id))
      );

      this.setState({
        feedEntrys: feed,
        feedLoaded: true,
        hasMore:
          data.data.length !== 0 && data.data.length >= this.state.batchSize,
      });
    }
  };

  requestNewQuery = () => {
    this.props.history.push({
      search: qs.stringify({
        suserId: this.state.selectedUserId,
        selemId: this.state.selectedElementId,
        stype: this.state.selectedType,
      }),
    });
    this.setState(
      { hasMore: true, feedEntrys: [], feedLoaded: false },
      this.requestFeed
    );
  };

  handleEventTypeSelect = (eventType: string) => {
    this.setState({ selectedType: eventType }, this.requestNewQuery);
  };

  handleElemSelect = (elemId: string) => {
    this.setState({ selectedElementId: elemId }, this.requestNewQuery);
  };

  handleUserSelect = (userId: string) => {
    this.setState({ selectedUserId: userId }, this.requestNewQuery);
  };

  handleError = (err: any) => {
    console.error(err);
  };
  render() {
    return (
      <>
        <div
          className="subscreen-main-container"
          ref={this.rootRef}
          onClickCapture={this.handleListClick}
        >
          <div className="subscreen-box-container">
            <Title title="Bereichsfeed" />
            <div className="search-bar-wrapper">
              <SingleSelector
                searchTextPlaceholder="Event-Typ"
                arr={[
                  { name: "alle", value: null, color: "#ffd025" },
                  { name: "Buchungen", value: "booking" },
                  { name: "Stornierungen", value: "cancellation" },
                  { name: "Fehlgeschlagene Zahlungen", value: "paymentfailed" },
                  {
                    name: "Bereichsmitgliedschaften",
                    value: "branchmembership",
                  },
                ]}
                selectedElem={this.state.selectedEventTypeElem}
                onSelect={(eventElem: any) => {
                  this.setState({
                    selectedEventTypeElem: eventElem,
                  });
                  this.handleEventTypeSelect(eventElem.value);
                }}
              />
              <SingleSelector
                searchTextPlaceholder="Kurs/Mitgliedschaft"
                arr={this.getElementOptions()}
                selectedElem={this.state.selectedElement}
                onSearch={this.handleElementSearch}
                onLoadMore={this.handleLoadMoreElements}
                hasMore={this.state.elementHasMore}
                loading={this.state.elementsLoading}
                onSelect={(eventElem: any) => {
                  this.setState({
                    selectedElement: eventElem,
                  });
                  this.handleElemSelect(eventElem.value);
                }}
              />
              <SingleSelector
                searchTextPlaceholder="Nutzer"
                arr={this.state.userOptions}
                selectedElem={this.state.selectedUserElem}
                onSelect={(eventElem: any) => {
                  this.setState({
                    selectedUserElem: eventElem,
                  });
                  this.handleUserSelect(eventElem.value);
                }}
              />
            </div>

            <InfiniteScroll
              dataLength={this.state.feedEntrys.length}
              next={this.requestFeed}
              hasMore={this.state.hasMore}
              loader={<></>}
              height={window.innerHeight - 300}
              scrollThreshold={"200px"}
              className="infinity-scroll-feed"
            >
              <table className="user-table no-margin">
                <thead className="feed-thead">
                  <tr>
                    <th>Zeitpunkt</th>
                    <th>Nachricht</th>
                  </tr>
                </thead>
                <tbody>
                  {this.state.feedEntrys.map((feed, index) => (
                    <tr key={index} onClick={() => {}}>
                      <td>
                        {dateTosimpleDateStr(
                          firebaseDateToDateObj(feed.createdAt)
                        )}
                      </td>
                      <td>
                        <FeedEntry feed={feed} />
                      </td>
                    </tr>
                  ))}
                  {!this.state.feedLoaded && (
                    <>
                      <tr>
                        <td>
                          <div className="f-skeleton f-skeleton-text f-skeleton-text-37"></div>
                        </td>
                        <td>
                          <div className="f-skeleton f-skeleton-text f-skeleton-text-60"></div>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <div className="f-skeleton f-skeleton-text f-skeleton-text-40"></div>
                        </td>
                        <td>
                          <div className="f-skeleton f-skeleton-text f-skeleton-text-50"></div>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <div className="f-skeleton f-skeleton-text f-skeleton-text-35"></div>
                        </td>
                        <td>
                          <div className="f-skeleton f-skeleton-text f-skeleton-text-60"></div>
                        </td>
                      </tr>
                    </>
                  )}
                  {this.state.feedEntrys.length === 0 &&
                    this.state.feedLoaded && (
                      <tr>
                        <td colSpan={2} className="center-text">
                          <div>
                            Mit diesem Filter wurden keine Einträge gefunden.
                          </div>
                        </td>
                      </tr>
                    )}
                  {this.state.hasMore && this.state.feedLoaded && (
                    <tr>
                      <td colSpan={2}>
                        <div onClick={this.requestFeed}>
                          Weitere Einträge laden
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </InfiniteScroll>
          </div>
        </div>
      </>
    );
  }
}
